import { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { API_BASE } from '../api';

export function useChat(token) {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatStatus, setChatStatus] = useState('offline');
  const [chatError, setChatError] = useState('');
  const chatEndRef = useRef(null);
  const socketRef = useRef();

  const fetchChatMessages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunde inte hämta meddelanden.');
      if (!Array.isArray(data)) throw new Error('Oväntat svar från servern.');
      setChatMessages(data);
      setChatError('');
    } catch (err) {
      console.error('Error fetching chat:', err);
      setChatError(err.message || 'Kunde inte hämta meddelanden.');
    }
  }, [token]);

  const sendChatMessage = useCallback((e) => {
    e.preventDefault();
    const message = chatInput.trim();
    if (!message) return;
    if (!socketRef.current?.connected) {
      setChatError('Chatten är inte ansluten ännu. Försök igen om en stund.');
      return;
    }
    socketRef.current.emit('sendMessage', message, (result) => {
      if (!result?.ok) setChatError(result?.error || 'Meddelandet kunde inte skickas.');
    });
    setChatInput('');
  }, [chatInput]);

  const clearChat = useCallback(async () => {
    if (!confirm('Är du säker på att du vill tömma hela chatten? Detta går inte att ångra.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/messages`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Kunde inte tömma chatten.');
      setChatMessages([]);
      alert('Chatten har tömts.');
    } catch (err) {
      alert(err.message);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;

    setChatStatus('connecting');
    setChatError('');
    const socket = io({ auth: { token } });
    socketRef.current = socket;
    socket.on('connect', () => {
      setChatStatus('connected');
      setChatError('');
    });
    socket.on('connect_error', (err) => {
      setChatStatus('error');
      setChatError(err.message || 'Kunde inte ansluta till chatten.');
    });
    socket.on('disconnect', () => setChatStatus('offline'));
    socket.on('newMessage', (msg) => {
      setChatMessages(prev => prev.some(item => item.id === msg.id) ? prev : [...prev, msg]);
    });
    socket.on('chatCleared', () => {
      setChatMessages([]);
    });
    fetchChatMessages();

    return () => {
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = undefined;
    };
  }, [token]);

  return {
    chatMessages, setChatMessages, chatInput, setChatInput,
    chatStatus, chatError, setChatError,
    sendChatMessage, clearChat, chatEndRef
  };
}
